"use client";
import { ConnectionState, Room, RoomEvent, RemoteParticipant } from "livekit-client";
import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import type { ExcalidrawSyncEvent } from "@client/components/meet/Whiteboard";
import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";
import type { Collaborator, CollaboratorPointer, SocketId } from "@excalidraw/excalidraw/types";
import { exportExcalidrawBlob } from "@client/components/meet/exportExcalidrawBlob";
import { colorForNotesIdentity } from "@client/utils/notesPresence";

/**
 * Manages whiteboard state and synchronization.
 */
interface UseWhiteboardOptions {
  room: Room;
  isModerator: boolean;
  liveCursorsDisabled?: boolean;
}

interface UseWhiteboardReturn {
  remoteElements: readonly ExcalidrawElement[];
  handleElementsChange: (event: ExcalidrawSyncEvent) => void;
  handleWhiteboardDataReceived: (message: Record<string, unknown>, participant?: RemoteParticipant) => void;
  handlePointerUpdate: (pointer: CollaboratorPointer) => void;
  toggleLockForParticipants: () => void;
  lockedForParticipants: boolean;
  collaborators: Map<SocketId, Collaborator>;
  createFinalSnapshot: () => Promise<Blob | null>;
}

const PUBLISH_OPTIONS = { reliable: true } as const;
const textEncoder = new TextEncoder();

/**
 * Manages whiteboard state and synchronization.
 */
function participantIsModerator(participant?: RemoteParticipant): boolean {
  if (!participant?.metadata) return false;
  try {
    return JSON.parse(participant.metadata).isModerator === true;
  } catch {
    return false;
  }
}

export const useWhiteboard = ({
  room,
  isModerator,
  liveCursorsDisabled = false
}: UseWhiteboardOptions): UseWhiteboardReturn => {
  const [elements, setElements] = useState<readonly ExcalidrawElement[]>([]);
  const [lockedForParticipants, setLockedForParticipants] = useState(false);
  const [remoteCollaborators, setRemoteCollaborators] = useState<Map<string, Collaborator>>(new Map());
  const elementsRef = useRef<readonly ExcalidrawElement[]>([]);
  const lockedRef = useRef(false);
  const isModeratorRef = useRef(isModerator);
  const lastPointerPublishRef = useRef(0);
  const liveCursorsDisabledRef = useRef(liveCursorsDisabled);

  useEffect(() => {
    liveCursorsDisabledRef.current = liveCursorsDisabled;
    if (liveCursorsDisabled) {
      setRemoteCollaborators(new Map());
    }
  }, [liveCursorsDisabled]);

  useEffect(() => {
    elementsRef.current = elements;
  }, [elements]);

  // Stable room reference
  const roomRef = useRef(room);
  useEffect(() => {
    roomRef.current = room;
  }, [room]);

  useEffect(() => {
    isModeratorRef.current = isModerator;
  }, [isModerator]);

  const publishWhiteboardData = useCallback((data: object) => {
    const currentRoom = roomRef.current;
    if (!currentRoom || currentRoom.state !== ConnectionState.Connected) return;
    try {
      currentRoom.localParticipant.publishData(textEncoder.encode(JSON.stringify(data)), PUBLISH_OPTIONS);
    } catch {
      // Room may disconnect while publishing — ignore
    }
  }, []);

  const setLocked = useCallback(
    (locked: boolean, broadcast: boolean) => {
      lockedRef.current = locked;
      setLockedForParticipants(locked);
      if (broadcast) publishWhiteboardData({ type: "whiteboard-lock-state", locked });
    },
    [publishWhiteboardData]
  );

  const toggleLockForParticipants = useCallback(() => {
    if (!isModeratorRef.current) return;
    setLocked(!lockedRef.current, true);
  }, [setLocked]);

  const handlePointerUpdate = useCallback((pointer: CollaboratorPointer) => {
    if (liveCursorsDisabledRef.current) return;
    const now = Date.now();
    if (now - lastPointerPublishRef.current < 60) return;
    lastPointerPublishRef.current = now;
    const participant = roomRef.current.localParticipant;
    try {
      if (roomRef.current.state !== ConnectionState.Connected) return;
      participant.publishData(textEncoder.encode(JSON.stringify({ type: "whiteboard-cursor", pointer })), {
        reliable: false
      });
    } catch {
      // Ignore a pointer update racing with room disconnection.
    }
  }, []);

  // Request sync once the room is connected
  useEffect(() => {
    if (!room) return;

    const requestSync = () => {
      publishWhiteboardData({ type: "excalidraw-request-sync" });
    };

    if (room.state === ConnectionState.Connected) {
      requestSync();
    }

    room.on(RoomEvent.Connected, requestSync);
    return () => {
      room.off(RoomEvent.Connected, requestSync);
    };
  }, [room, publishWhiteboardData]);

  const handleElementsChange = useCallback(
    (event: ExcalidrawSyncEvent) => {
      if (lockedRef.current && !isModeratorRef.current) return;
      elementsRef.current = event.elements;
      setElements(event.elements);
      publishWhiteboardData({
        type: "excalidraw-sync",
        elements: event.elements,
        participantId: event.participantId
      });
    },
    [publishWhiteboardData]
  );

  const handleWhiteboardDataReceived = useCallback(
    (message: Record<string, unknown>, participant?: RemoteParticipant) => {
      const identity = participant?.identity;
      if (message.type === "whiteboard-cursor" && identity) {
        if (liveCursorsDisabledRef.current) return;
        const pointer = message.pointer as CollaboratorPointer | undefined;
        if (!pointer || !Number.isFinite(pointer.x) || !Number.isFinite(pointer.y)) return;
        const color = colorForNotesIdentity(identity);
        setRemoteCollaborators((prev) => {
          const next = new Map(prev);
          next.set(identity, {
            id: identity,
            socketId: identity as SocketId,
            username: participant?.name?.trim() || identity,
            pointer,
            color: { background: color.bg, stroke: color.caret },
            isInCall: true
          });
          return next;
        });
        return;
      }

      if (message.type === "whiteboard-lock-state") {
        if (participantIsModerator(participant) && typeof message.locked === "boolean") {
          setLocked(message.locked, false);
        }
        return;
      }

      if (message.type === "excalidraw-sync" && message.elements) {
        if (lockedRef.current && !participantIsModerator(participant)) return;
        const nextElements = message.elements as readonly ExcalidrawElement[];
        elementsRef.current = nextElements;
        setElements(nextElements);
      } else if (message.type === "excalidraw-request-sync") {
        if (isModeratorRef.current) {
          publishWhiteboardData({ type: "whiteboard-lock-state", locked: lockedRef.current });
        }
        if (elementsRef.current.length > 0) {
          publishWhiteboardData({
            type: "excalidraw-sync",
            elements: elementsRef.current,
            participantId: roomRef.current.localParticipant.sid
          });
        }
      }
    },
    [publishWhiteboardData, setLocked]
  );

  useEffect(() => {
    const handleParticipantDisconnected = (participant: RemoteParticipant) => {
      setRemoteCollaborators((prev) => {
        if (!prev.has(participant.identity)) return prev;
        const next = new Map(prev);
        next.delete(participant.identity);
        return next;
      });
    };
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
    return () => {
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
    };
  }, [room]);

  const createFinalSnapshot = useCallback(async (): Promise<Blob | null> => {
    const activeElements = elementsRef.current.filter((element) => !element.isDeleted);
    if (activeElements.length === 0) return null;
    return exportExcalidrawBlob({
      elements: activeElements,
      appState: { exportBackground: true, exportWithDarkMode: false },
      files: {},
      mimeType: "image/png"
    });
  }, []);

  return useMemo(
    () => ({
      remoteElements: elements,
      handleElementsChange,
      handleWhiteboardDataReceived,
      handlePointerUpdate,
      toggleLockForParticipants,
      lockedForParticipants,
      collaborators: new Map(
        Array.from(remoteCollaborators.entries()).map(([identity, collaborator]) => [
          identity as SocketId,
          collaborator
        ])
      ),
      createFinalSnapshot
    }),
    [
      elements,
      handleElementsChange,
      handleWhiteboardDataReceived,
      handlePointerUpdate,
      toggleLockForParticipants,
      lockedForParticipants,
      remoteCollaborators,
      createFinalSnapshot
    ]
  );
};
